export interface PublishConnection {
  id: string;
  platform: string;
  status: string;
  scopes: string[];
  username: string;
  display_name?: string | null;
}

export type PublishPlatform = 'tiktok' | 'youtube' | 'instagram' | 'facebook';

export function isPublishPlatform(value: unknown): value is PublishPlatform {
  return value === 'tiktok' || value === 'youtube' || value === 'instagram' || value === 'facebook';
}

/** Latest publish `media_kind` per platform for a clip card. */
export type PublishMediaKinds = Partial<Record<PublishPlatform, string | null>>;

export interface PublishQueueRef {
  id: string;
  name: string;
}

export interface PublishRecord {
  id: string;
  /** Backend wire value. Narrow with `isPublishPlatform` before using it as
   * an actionable publishing target; analytics rows may use other platforms. */
  platform: string;
  status: string;
  fail_reason?: string | null;
  clip_id?: string | null;
  social_connection_id?: string | null;
  provider_post_id?: string | null;
  /** `'reel'`, `'video'` or `'photos'` on Facebook rows. */
  facebook_post_kind?: string | null;
  caption?: string | null;
  title?: string | null;
  description?: string | null;
  privacy_status?: string | null;
  made_for_kids?: boolean | null;
  scheduled_at?: string | null;
  cancelled_at?: string | null;
  createdAt?: string | null;
  /** `'video'` or `'carousel'` (image list). Absent on older backends. */
  media_kind?: string | null;
  image_count?: number | null;
  publish_queue?: PublishQueueRef | null;
}

export type ActionablePublishRecord = Omit<PublishRecord, 'platform'> & {
  platform: PublishPlatform;
};

export interface PublishDialogTarget {
  clipId: string;
  platform: PublishPlatform;
  initialWhen?: 'now' | 'later';
  initialDatetimeLocal?: string;
}

export type PublishEligibility =
  | 'eligible'
  | 'not_rendered'
  | 'no_connection'
  | 'reconnect_required'
  | 'quota_exceeded';

export type PublishUiStatus = 'publishing' | 'in_drafts' | 'published' | 'failed' | 'none';

export const PUBLISH_SCOPE = 'video.upload';
export const TIKTOK_UPLOAD_SCOPE = 'video.upload';
export const TIKTOK_DIRECT_POST_SCOPE = 'video.publish';
